import { api, apiBaseUrl } from '../lib/api';
import { useAuthStore } from '../store/auth';
import type { ReportDirectionTab, ReportFilters, ReportFormat, ReportKind } from '@marnic/shared/reports';

export interface ReportQueryState {
  from: string;
  to: string;
  timeFrom: string;
  timeTo: string;
  tab: ReportDirectionTab;
  phone: string;
  agentId: string;
  campaignId: string;
  queueId: string;
  ringGroupId: string;
  twilioNumberId: string;
  direction: string;
  status: string;
  hasRecording: string;
  /** Talk-time window in seconds; min inclusive, max exclusive, blank = open. */
  minDuration: string;
  maxDuration: string;
}

export const FILTER_KEYS = [
  'phone', 'agentId', 'campaignId', 'queueId', 'ringGroupId',
  'twilioNumberId', 'direction', 'status', 'hasRecording',
  'minDuration', 'maxDuration',
] as const satisfies readonly (keyof ReportQueryState)[];

export type FilterKey = typeof FILTER_KEYS[number];

export function defaultReportState(): ReportQueryState {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: toDateInput(start),
    to: toDateInput(now),
    timeFrom: '00:00',
    timeTo: '24:00',
    tab: 'total',
    phone: '',
    agentId: '',
    campaignId: '',
    queueId: '',
    ringGroupId: '',
    twilioNumberId: '',
    direction: '',
    status: '',
    hasRecording: '',
    minDuration: '',
    maxDuration: '',
  };
}

const SERIALIZABLE_KEYS: (keyof ReportQueryState)[] = [
  'from', 'to', 'timeFrom', 'timeTo', 'tab',
  ...FILTER_KEYS,
];

export function parseReportState(params: URLSearchParams): ReportQueryState {
  const defaults = defaultReportState();
  const next: ReportQueryState = { ...defaults };
  const writable = next as unknown as Record<string, string>;
  for (const key of SERIALIZABLE_KEYS) {
    const value = params.get(key);
    if (value !== null) writable[key] = value;
  }
  return next;
}

export function serializeReportState(state: ReportQueryState): URLSearchParams {
  const defaults = defaultReportState();
  const params = new URLSearchParams();
  for (const key of SERIALIZABLE_KEYS) {
    const value = state[key];
    if (value && value !== defaults[key]) params.set(key, value);
  }
  return params;
}

export function countActiveFilters(state: ReportQueryState): number {
  return FILTER_KEYS.reduce((acc, key) => acc + (state[key] ? 1 : 0), 0);
}

export function clearFilters(state: ReportQueryState): ReportQueryState {
  const next = { ...state };
  const writable = next as unknown as Record<string, string>;
  for (const key of FILTER_KEYS) writable[key] = '';
  return next;
}

export function buildReportSearch(state: ReportQueryState, extra: Record<string, string | number | boolean | undefined> = {}): string {
  const params = new URLSearchParams();
  const from = new Date(`${state.from}T00:00:00`);
  const to = new Date(`${state.to}T23:59:59`);
  params.set('from', from.toISOString());
  params.set('to', to.toISOString());
  params.set('timeFrom', state.timeFrom);
  params.set('timeTo', state.timeTo);
  params.set('tab', state.tab);
  setIf(params, 'phone', state.phone);
  setIf(params, 'agentId', state.agentId);
  setIf(params, 'campaignId', state.campaignId);
  setIf(params, 'queueId', state.queueId);
  setIf(params, 'ringGroupId', state.ringGroupId);
  setIf(params, 'twilioNumberId', state.twilioNumberId);
  setIf(params, 'direction', state.direction);
  setIf(params, 'status', state.status);
  setIf(params, 'hasRecording', state.hasRecording);
  setIf(params, 'minDuration', state.minDuration);
  setIf(params, 'maxDuration', state.maxDuration);
  for (const [key, value] of Object.entries(extra)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  return `?${params.toString()}`;
}

export function filtersFromState(state: ReportQueryState): Partial<ReportFilters> {
  return {
    dateRange: {
      from: new Date(`${state.from}T00:00:00`).toISOString(),
      to: new Date(`${state.to}T23:59:59`).toISOString(),
    },
    timeRange: { from: state.timeFrom, to: state.timeTo },
    ...(state.phone ? { phone: state.phone } : {}),
    ...(state.agentId ? { agentId: state.agentId } : {}),
    ...(state.campaignId ? { campaignId: state.campaignId } : {}),
    ...(state.queueId ? { queueId: state.queueId } : {}),
    ...(state.ringGroupId ? { ringGroupId: state.ringGroupId } : {}),
    ...(state.twilioNumberId ? { twilioNumberId: state.twilioNumberId } : {}),
    ...(state.direction ? { direction: state.direction as 'inbound' | 'outbound' } : {}),
    ...(state.status ? { status: state.status as ReportFilters['status'] } : {}),
    ...(state.hasRecording ? { hasRecording: state.hasRecording === 'true' } : {}),
    ...(state.minDuration ? { minDuration: Number(state.minDuration) } : {}),
    ...(state.maxDuration ? { maxDuration: Number(state.maxDuration) } : {}),
  };
}

export async function downloadReport(report: ReportKind, format: ReportFormat, state: ReportQueryState): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${apiBaseUrl}/reports/export${buildReportSearch(state, { report, format })}`, {
    headers: token ? { authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${report}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function emailReport(report: ReportKind, formats: ReportFormat[], state: ReportQueryState): Promise<void> {
  await api.post('/reports/calls/to-email', {
    report,
    formats,
    filters: filtersFromState(state),
  });
}

function setIf(params: URLSearchParams, key: string, value: string): void {
  if (value.trim()) params.set(key, value.trim());
}

function toDateInput(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function formatDuration(seconds: number | null | undefined): string {
  const total = Math.max(0, seconds ?? 0);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Compact talk-time label for axes, tooltips and filter chips: "45s", "2h 30m". */
export function formatShortDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total === 0) return '0';
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s > 0 && m < 10 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

export function reportLabel(kind: ReportKind): string {
  return kind.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}
